import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {Project, ProjectService} from '@mydocma/project';
import {SettingsService} from '../../../mydocma-settings/settings.service';
import {SettingNamesEnum} from '../../../mydocma-settings/setting-names.enum';
import {SettingTypeEnum} from '../../../mydocma-settings/setting-type.enum';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {filter} from 'rxjs/operators';
import {ConfigProvider} from "@mydocma/common";
import {IMmConfig} from "@docma-mm-core/project/project.interface";

@Injectable()
export class MmProjectSettingsService {
  private readonly maxProjectLevelSubject = new BehaviorSubject<number | null>(null);
  readonly maxProjectLevel$ = this.maxProjectLevelSubject.asObservable();
  private currentProject: Project | null = null;
  private moduleName: any;
  private maxLevelWriteChain: Promise<void> = Promise.resolve();

  private getCurrentProject(): Project {
    if (!this.currentProject?.id) {
      throw new Error('No current project selected.');
    }

    return this.currentProject;
  }

  constructor(
    private readonly settingsService: SettingsService,
    private readonly projectService: ProjectService,
    private configService: ConfigProvider<IMmConfig>,
  ) {
    this.moduleName = this.configService.getConfigData().appName;
    this.projectService.currentProject
      .pipe(filter((p) => !!p))
      .subscribe(async project => {
        this.currentProject = project;
        this.loadMaxLevelForProject();
      });
  }

  private normalizeLevel(value: unknown): number | null {
    if (value == null || value === '') {
      return null;
    }

    const parsed =
      typeof value === 'number'
        ? value
        : Number(value);

    return Number.isFinite(parsed) && parsed > 0
      ? Math.floor(parsed)
      : null;
  }

  private async loadMaxLevelForProject(): Promise<void> {
    const project = this.getCurrentProject();

    const rawValue = await this.settingsService.load(
      PersonalizationKeysEnum.MaxProjectStructureLevel,
      SettingNamesEnum.MaxProjectStructureLevel,
      project,
      this.moduleName,
      { scope: SettingTypeEnum.project }
    );

    this.maxProjectLevelSubject.next(this.normalizeLevel(rawValue));
  }

  async getMaxProjectStructureLevel(): Promise<number | null> {
    await this.loadMaxLevelForProject();
    return this.maxProjectLevelSubject.value;
  }

  async setMaxProjectStructureLevel(value: number | null): Promise<void> {
    const project = this.getCurrentProject();
    const normalized = this.normalizeLevel(value);

    const next = this.maxLevelWriteChain.then(async () => {
      // Backend DELETE on project-scope silently no-ops on settings-api;
      // persist "0" as the "no max level" sentinel (normalizeLevel maps 0 -> null on read).
      const persistedValue = normalized == null ? '0' : String(normalized);
      await this.settingsService.save(
        persistedValue,
        PersonalizationKeysEnum.MaxProjectStructureLevel,
        SettingNamesEnum.MaxProjectStructureLevel,
        project,
        this.moduleName,
        { scope: SettingTypeEnum.project, throwOnError: true }
      );

      this.maxProjectLevelSubject.next(normalized);
    });

    this.maxLevelWriteChain = next.catch(() => undefined);
    return next;
  }
}
